import React, { useState } from 'react';
import apiClient from '../utils/apiClient';
import './ReconDBSubmitModal.css';

const ReconDBSubmitModal = ({ isOpen, onClose, language }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const translations = {
    en: {
      modalTitle: 'Submit Disassembly',
      field1Label: 'Game Title / Platform',
      field1Placeholder: 'e.g., Super Mario Bros. / NES',
      field2Label: 'Author / Link',
      field2Placeholder: 'e.g., John Doe / https://github.com/...',
      field3Label: 'File (Optional)',
      field3Hint: 'All file formats supported, max 50MB',
      submitButton: 'Submit',
      cancelButton: 'Cancel',
      submitting: 'Submitting...',
      successTitle: 'Thanks for Your Contribution!',
      successMessage: 'Your ReconDB submission has been received. You can view and track it using the link below:',
      copyLinkButton: 'Copy Link',
      linkCopied: 'Link copied!',
      closeButton: 'Close',
      fileTooLarge: 'File size cannot exceed 50MB',
      submissionError: 'Submission failed. Please try again later.'
    },
    zh: {
      modalTitle: '提交解编',
      field1Label: '游戏标题 / 平台',
      field1Placeholder: '例如：超级马里奥兄弟 / NES',
      field2Label: '解编作者 / 链接',
      field2Placeholder: '例如：张三 / https://github.com/...',
      field3Label: '文件（可选）',
      field3Hint: '支持所有文件格式，最大50MB',
      submitButton: '提交',
      cancelButton: '取消',
      submitting: '提交中...',
      successTitle: '感谢您的贡献！',
      successMessage: '您的ReconDB提交已收到。您可以通过以下链接查看和追踪：',
      copyLinkButton: '复制链接',
      linkCopied: '链接已复制！',
      closeButton: '关闭',
      fileTooLarge: '文件大小不能超过50MB',
      submissionError: '提交失败，请稍后重试。'
    }
  };

  const t = translations[language] || translations.en;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB = 50 * 1024 * 1024 bytes)
      if (file.size > 50 * 1024 * 1024) {
        alert(t.fileTooLarge);
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = new FormData();
    submitData.append('type', 'recondb_entry');
    submitData.append('title', formData.title);
    submitData.append('content', formData.description);
    submitData.append('language', language);
    
    if (formData.file) {
      submitData.append('attachment', formData.file);
    }

    try {
      const response = await apiClient.post('/feedback/threads', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const threadId = response.data.threadId;
      const threadLink = `${window.location.origin}/feedback?thread=${threadId}&lang=${language}`;
      
      setSubmissionResult({
        success: true,
        link: threadLink,
        threadId
      });
    } catch (error) {
      console.error('Error submitting ReconDB entry:', error);
      setSubmissionResult({
        success: false,
        error: t.submissionError
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    if (submissionResult && submissionResult.link) {
      navigator.clipboard.writeText(submissionResult.link);
      // Show temporary "copied" feedback
      const button = document.querySelector('.recondb-copy-link-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = t.linkCopied;
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }
  };

  const handleClose = () => {
    setFormData({ title: '', description: '', file: null });
    setSubmissionResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="recondb-modal-overlay" onClick={handleClose}>
      <div className="recondb-modal" onClick={(e) => e.stopPropagation()}>
        {!submissionResult ? (
          <>
            <div className="recondb-modal-header">
              <h2>{t.modalTitle}</h2>
              <button className="recondb-modal-close" onClick={handleClose}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="recondb-form">
              <div className="recondb-form-group">
                <label>{t.field1Label} *</label>
                <input 
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t.field1Placeholder}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="recondb-form-group">
                <label>{t.field2Label} *</label>
                <textarea 
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t.field2Placeholder}
                  rows="4"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="recondb-form-group">
                <label>{t.field3Label}</label>
                <div className="recondb-file-upload-wrapper">
                  <label className="recondb-file-upload-button">
                    <input 
                      type="file"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      disabled={isSubmitting}
                    />
                    <span className="button-text">
                      {formData.file ? formData.file.name : t.field3Label}
                    </span>
                  </label>
                  {formData.file && (
                    <button 
                      type="button" 
                      className="recondb-file-remove-btn"
                      onClick={() => setFormData({ ...formData, file: null })}
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  )}
                </div>
                <small className="recondb-form-hint">{t.field3Hint}</small>
              </div>

              <div className="recondb-form-actions">
                <button 
                  type="button" 
                  className="recondb-btn-cancel" 
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t.cancelButton}
                </button>
                <button 
                  type="submit" 
                  className="recondb-btn-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t.submitting : t.submitButton}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="recondb-success-container">
            {submissionResult.success ? (
              <>
                <div className="recondb-success-icon">
                  <svg viewBox="0 0 100 100" className="success-svg">
                    <circle className="success-circle" cx="50" cy="50" r="45" />
                    <path className="success-check" d="M25 50 L40 65 L75 30" />
                  </svg>
                </div>
                <h2>{t.successTitle}</h2>
                <p className="recondb-success-message">{t.successMessage}</p>
                <div className="recondb-link-container">
                  <input 
                    type="text" 
                    value={submissionResult.link} 
                    readOnly 
                    className="recondb-link-input"
                  />
                  <button 
                    className="recondb-copy-link-button"
                    onClick={handleCopyLink}
                  >
                    {t.copyLinkButton}
                  </button>
                </div>
                <button 
                  className="recondb-btn-close"
                  onClick={handleClose}
                >
                  {t.closeButton}
                </button>
              </>
            ) : (
              <>
                <div className="recondb-error-icon">✕</div>
                <h2>{submissionResult.error}</h2>
                <button 
                  className="recondb-btn-close"
                  onClick={handleClose}
                >
                  {t.closeButton}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconDBSubmitModal;
